import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";

/* --------------------------------------------------------
   포럼 본문용 작은 마크다운 렌더러 — 외부 의존성 없음.
   HTML 문자열을 끼워 넣지 않고 React 요소로만 만들기 때문에
   본문에 <script> 같은 것을 적어도 글자로만 보인다.

   지원: # 제목, 문단(줄바꿈 유지), **굵게**, *기울임*, ~~취소선~~, `코드`,
        ```코드 블록```, > 인용, - 목록, 1. 목록, | 표 |, ---, [링크](주소),
        ![설명](사진 주소), 한 줄에 유튜브 주소만 있으면 영상으로 넣는다.
   사진 폭: ![설명|60%](주소) 는 본문 폭의 60%, ![설명|480](주소) 는 480px.
   onImageResize 를 주면(SSTOA 미리보기) 한 줄짜리 사진 모서리를 끌어 폭을 바꿀 수 있다.
   -------------------------------------------------------- */

const FENCE = /^\s*```\s*([\w+#.-]*)\s*$/;
const HEADING = /^(#{1,4})\s+(.+?)\s*#*\s*$/;
const HR = /^\s*([-*_])(\s*\1){2,}\s*$/;
const QUOTE = /^\s*>\s?/;
const UL = /^\s*[-*+]\s+/;
const OL = /^\s*\d+[.)]\s+/;
const TABLE_SEP = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;
const IMAGE_LINE = /^\s*!\[([^\]]*)\]\(\s*([^)\s]+)(?:\s+"([^"]*)")?\s*\)\s*$/;
const YOUTUBE_LINE =
  /^\s*https?:\/\/(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?(?:\S*&)?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})\S*\s*$/;

const INLINE =
  /(`+)([\s\S]+?)\1|!\[([^\]]*)\]\(\s*([^)\s]+)\s*\)|\[([^\]]+)\]\(\s*([^)\s]+)\s*\)|\*\*([\s\S]+?)\*\*|~~([\s\S]+?)~~|\*(?!\s)([^*]+?)\*|(https?:\/\/[^\s<]*[^\s<.,;:!?)'"\]])/g;

/* 링크·사진 주소는 http(s) · 사이트 안 경로 · 메일만 받는다(javascript: 등 차단) */
function safeHref(url) {
  return /^(https?:\/\/|mailto:|\/(?!\/)|#)/i.test(url) ? url : null;
}
function safeSrc(url) {
  return /^(https?:\/\/|\/(?!\/))/i.test(url) ? url : null;
}

/* "설명|60%" → { alt: "설명", width: "60%" } */
function parseAlt(raw) {
  const m = (raw || "").match(/^(.*?)\|\s*(\d{1,4})(px|%)?\s*$/);
  if (!m) return { alt: raw || "", width: undefined };
  const n = Number(m[2]);
  const width = m[3] === "%" ? `${Math.min(100, Math.max(5, n))}%` : `${n}px`;
  return { alt: m[1].trim(), width };
}

/* 오른쪽 아래 모서리를 끌어 폭을 바꾸는 사진. 사진이 가운데 정렬이라
   끈 거리의 두 배만큼 폭이 바뀌어야 모서리가 손가락(커서)을 따라온다. */
function ResizableImage({ src, alt, width, onResize }) {
  const boxRef = useRef(null);
  const drag = useRef(null);
  const [live, setLive] = useState(null);

  function down(e) {
    e.preventDefault();
    e.stopPropagation();
    const box = boxRef.current;
    const full = box.parentElement.clientWidth || 1;
    drag.current = { x: e.clientX, w: box.getBoundingClientRect().width, full };
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function move(e) {
    const d = drag.current;
    if (!d) return;
    const w = Math.min(d.full, Math.max(40, d.w + (e.clientX - d.x) * 2));
    setLive(Math.round((w / d.full) * 100));
  }
  function up() {
    const pct = live;
    drag.current = null;
    setLive(null);
    if (Number.isFinite(pct)) onResize(Math.min(100, Math.max(5, pct)));
  }

  return (
    <span ref={boxRef} className="md__resize" style={{ width: live != null ? `${live}%` : width }}>
      <img src={src} alt={alt} loading="lazy" draggable={false} />
      {live != null && <span className="md__resize-size">{live}%</span>}
      <span
        className="md__resize-handle"
        title="끌어서 크기 조절"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
      />
    </span>
  );
}

function A({ href, children }) {
  const url = safeHref(href);
  if (!url) return <>{children}</>;
  if (url.startsWith("/") && !url.startsWith("/api/")) return <Link to={url}>{children}</Link>;
  if (url.startsWith("#") || url.startsWith("/")) return <a href={url}>{children}</a>;
  return <a href={url} target="_blank" rel="noopener noreferrer">{children}</a>;
}

function inline(text, key = "i") {
  const out = [];
  let last = 0;
  let n = 0;
  const re = new RegExp(INLINE.source, "g"); // 굵게 안의 링크처럼 재귀로 부르므로 호출마다 따로 둔다
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${key}-${n++}`;
    if (m[1]) out.push(<code key={k}>{m[2].trim()}</code>);
    else if (m[4] !== undefined) {
      const src = safeSrc(m[4]);
      const { alt, width } = parseAlt(m[3]);
      out.push(src ? <img key={k} src={src} alt={alt} loading="lazy" style={width ? { width } : undefined} /> : m[0]);
    } else if (m[6] !== undefined) out.push(<A key={k} href={m[6]}>{inline(m[5], k)}</A>);
    else if (m[7] !== undefined) out.push(<strong key={k}>{inline(m[7], k)}</strong>);
    else if (m[8] !== undefined) out.push(<del key={k}>{inline(m[8], k)}</del>);
    else if (m[9] !== undefined) out.push(<em key={k}>{inline(m[9], k)}</em>);
    else if (m[10]) out.push(<A key={k} href={m[10]}>{m[10]}</A>);
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/* 줄바꿈을 <br> 로 살린 인라인 */
function lines(textLines, key) {
  return textLines.flatMap((l, i) => (i === 0 ? inline(l, `${key}-${i}`) : [<br key={`${key}-br${i}`} />, ...inline(l, `${key}-${i}`)]));
}

function CodeBlock({ lang, code }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* 클립보드 권한이 없으면 조용히 넘어간다 */
    }
  }
  return (
    <div className="md__code">
      <div className="md__code-bar">
        <span>{lang || "code"}</span>
        <button type="button" onClick={copy}>{copied ? "복사됨" : "복사"}</button>
      </div>
      <pre><code>{code}</code></pre>
    </div>
  );
}

function splitRow(line) {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  return s.split("|").map((c) => c.trim());
}

function isBlockStart(line, next) {
  return (
    FENCE.test(line) || HEADING.test(line) || HR.test(line) || QUOTE.test(line) ||
    UL.test(line) || OL.test(line) || IMAGE_LINE.test(line) || YOUTUBE_LINE.test(line) ||
    (line.includes("|") && next !== undefined && TABLE_SEP.test(next))
  );
}

function blocks(src, keyBase = "b", onImageResize) {
  const rows = src.replace(/\r\n?/g, "\n").split("\n");
  const out = [];
  let i = 0;
  const key = () => `${keyBase}${out.length}`;

  while (i < rows.length) {
    const line = rows[i];

    if (!line.trim()) { i += 1; continue; }

    let m = line.match(FENCE);
    if (m) {
      const lang = m[1];
      const body = [];
      i += 1;
      while (i < rows.length && !/^\s*```\s*$/.test(rows[i])) { body.push(rows[i]); i += 1; }
      i += 1; // 닫는 ``` (없으면 끝까지 코드로 본다)
      out.push(<CodeBlock key={key()} lang={lang} code={body.join("\n")} />);
      continue;
    }

    m = line.match(HEADING);
    if (m) {
      const Tag = ["h2", "h2", "h3", "h4"][m[1].length - 1];
      out.push(<Tag key={key()}>{inline(m[2], key())}</Tag>);
      i += 1;
      continue;
    }

    if (HR.test(line)) { out.push(<hr key={key()} />); i += 1; continue; }

    m = line.match(YOUTUBE_LINE);
    if (m) {
      out.push(
        <div key={key()} className="md__video">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${m[1]}`}
            title="YouTube 영상"
            loading="lazy"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
      i += 1;
      continue;
    }

    m = line.match(IMAGE_LINE);
    if (m) {
      const src = safeSrc(m[2]);
      const { alt, width } = parseAlt(m[1]);
      const caption = m[3] || alt;
      const line = i;
      if (src) {
        out.push(
          <figure key={key()} className="md__figure">
            {onImageResize ? (
              <ResizableImage src={src} alt={alt} width={width} onResize={(pct) => onImageResize(line, pct)} />
            ) : (
              <img src={src} alt={alt} loading="lazy" style={width ? { width } : undefined} />
            )}
            {caption && <figcaption>{caption}</figcaption>}
          </figure>
        );
      }
      i += 1;
      continue;
    }

    if (QUOTE.test(line)) {
      const body = [];
      while (i < rows.length && QUOTE.test(rows[i])) { body.push(rows[i].replace(QUOTE, "")); i += 1; }
      const k = key();
      out.push(<blockquote key={k}>{blocks(body.join("\n"), `${k}q`)}</blockquote>);
      continue;
    }

    if (UL.test(line) || OL.test(line)) {
      const ordered = OL.test(line);
      const re = ordered ? OL : UL;
      const items = [];
      while (i < rows.length && rows[i].trim()) {
        if (re.test(rows[i])) items.push([rows[i].replace(re, "")]);
        else if (/^\s+\S/.test(rows[i]) && items.length) items[items.length - 1].push(rows[i].trim());
        else break;
        i += 1;
      }
      const k = key();
      const Tag = ordered ? "ol" : "ul";
      out.push(<Tag key={k}>{items.map((it, j) => <li key={j}>{lines(it, `${k}-${j}`)}</li>)}</Tag>);
      continue;
    }

    if (line.includes("|") && i + 1 < rows.length && TABLE_SEP.test(rows[i + 1])) {
      const head = splitRow(line);
      i += 2;
      const body = [];
      while (i < rows.length && rows[i].includes("|") && rows[i].trim()) { body.push(splitRow(rows[i])); i += 1; }
      const k = key();
      out.push(
        <div key={k} className="md__table">
          <table>
            <thead><tr>{head.map((c, j) => <th key={j}>{inline(c, `${k}h${j}`)}</th>)}</tr></thead>
            <tbody>
              {body.map((r, ri) => (
                <tr key={ri}>{head.map((_, j) => <td key={j}>{inline(r[j] || "", `${k}${ri}-${j}`)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    const para = [line];
    i += 1;
    while (i < rows.length && rows[i].trim() && !isBlockStart(rows[i], rows[i + 1])) { para.push(rows[i]); i += 1; }
    const k = key();
    out.push(<p key={k}>{lines(para, k)}</p>);
  }
  return out;
}

/* onImageResize(줄 번호, 퍼센트) — 줄 번호는 source 를 줄 단위로 나눈 위치(맨 바깥 블록만) */
export default function Markdown({ source, className = "", onImageResize }) {
  return <div className={`md ${className}`.trim()}>{blocks(source || "", "b", onImageResize)}</div>;
}
